import {
  type MouseEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { Plus } from '@phosphor-icons/react';

import LinkButton from '_components/UI/LinkButton';
import {
  type DeferredNavigationProps,
  isModifiedClick,
  toRelativeUrl,
} from '_utils/GA4/clickTracking';
import {
  BUNDLE_STRIP_ATC_SOURCE_BY_PLACEMENT,
  type BundleStripPlacement,
  trackBundleAtcClick,
  trackBundleCardClick,
  trackBundleCtaClick,
} from '_utils/GA4/trackBundleStripEvent';
import useCustomer from '@adapters/bigcommerce/hooks/customer/useCustomer';
import type { BundlePromotion } from '@adapters/bigcommerce/transforms/bundlePromotion';
import type { BundleStripData } from '@adapters/prismic/transforms/bundleStrip';
import { useLocale } from '@hooks/useLocale';
import { usePrice } from '@hooks/usePrice';
import { localizeHref } from '@utils/localizeHref';
import { getGroupKeyByCustomerGroupId } from '@utils/selectPriceByGroup';

import BundleAddModal from './BundleAddModal';
import BundleCard from './BundleCard';
import BundleProductCard, { BundleProductPrice } from './BundleProductCard';
import {
  BundleLinePrice,
  BundleLineQuote,
  PriceGroupKey,
  applyBundlePromotion,
  getBundleLinePrice,
  getColourwayLinePrice,
  isBundlePromotionLive,
  sumBundleLines,
} from './bundlePricing';
import { remainingLines, useAddBundleToCart } from './useAddBundleToCart';
import { BundleSelectionLine, useBundleSelection } from './useBundleSelection';

import styles from './BundleStrip.module.scss';

export interface BundleStripProps extends BundleStripData {
  placement: BundleStripPlacement;
  priority?: boolean;
}

const trackLinkClick = (
  e: MouseEvent<HTMLAnchorElement>,
  href: string,
  track: (deferral: DeferredNavigationProps) => void
) => {
  if (isModifiedClick(e)) {
    track({});
    return;
  }
  e.preventDefault();
  track({
    eventCallback: () => {
      window.location.href = href;
    },
  });
};

const useViewerPricing = (
  promotion: BundlePromotion | null
): { priceGroupKey: PriceGroupKey; promotion: BundlePromotion | null } => {
  const [hasMounted, setHasMounted] = useState(false);
  const { customer } = useCustomer();
  useEffect(() => setHasMounted(true), []);

  const customerGroupId = hasMounted ? customer?.customerGroupId : null;
  const isLive =
    promotion !== null &&
    isBundlePromotionLive(
      promotion,
      hasMounted ? new Date() : new Date(promotion.fetchedAt),
      customerGroupId
    );

  return {
    priceGroupKey: customerGroupId
      ? getGroupKeyByCustomerGroupId(customerGroupId)
      : 'guest',
    promotion: isLive ? promotion : null,
  };
};

const BundleStrip = ({
  heading,
  description,
  badgeLabel,
  image,
  ctaLabel,
  buildYourOwn,
  coOpBrand,
  products,
  promotion: authoredPromotion,
  placement,
  priority = false,
}: BundleStripProps) => {
  const { t } = useTranslation('common');
  const locale = useLocale();
  const { asPath } = useRouter();
  const headingId = useId();
  const formatPrice = usePrice();
  const atcSource = BUNDLE_STRIP_ATC_SOURCE_BY_PLACEMENT[placement];
  const eventPage = asPath.split('?')[0];
  const eventData = useMemo(
    () => ({ interactionLocation: placement, heading, eventPage }),
    [placement, heading, eventPage]
  );
  const addLabel = ctaLabel || t('bundleStrip.addBundle');
  const { priceGroupKey, promotion } = useViewerPricing(authoredPromotion);

  const [armed, setArmed] = useState(false);
  const [pendingAdd, setPendingAdd] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selection = useBundleSelection(products, armed);
  const {
    addBundle,
    status: addStatus,
    failed,
    addedUnits,
    reset,
  } = useAddBundleToCart({ coOpBrand, atcSource });

  const pendingLines = useMemo(
    () => remainingLines(selection.lines, addedUnits),
    [selection.lines, addedUnits]
  );
  const canAdd =
    selection.status === 'ready' &&
    pendingLines.every(({ canAddToCart }) => canAddToCart);

  const stripLines = useMemo(
    () =>
      products.map(
        ({ product, variantSku }, i): BundleLineQuote => ({
          ...getBundleLinePrice(product, variantSku, priceGroupKey),
          quantity: selection.lines[i]?.quantity ?? 1,
        })
      ),
    [products, priceGroupKey, selection.lines]
  );

  const selectedPrices = useMemo(
    () =>
      new Map(
        selection.lines.map((line, i): [string, BundleLinePrice] => [
          line.code,
          getColourwayLinePrice(
            products[i].product,
            line.colours.find(({ id }) => id === line.selectedColourId)?.name,
            priceGroupKey
          ) ?? stripLines[i],
        ])
      ),
    [selection.lines, products, priceGroupKey, stripLines]
  );

  const promotedStripLines = applyBundlePromotion(stripLines, promotion);
  const stripTotals = sumBundleLines(promotedStripLines);
  const pendingQuotes = useMemo(() => {
    const quotes = pendingLines.flatMap((line): [string, BundleLineQuote][] => {
      const price = selectedPrices.get(line.code);
      return price ? [[line.code, { ...price, quantity: line.quantity }]] : [];
    });
    const promoted = applyBundlePromotion(
      quotes.map(([, quote]) => quote),
      promotion
    );
    return new Map(quotes.map(([code], i) => [code, promoted[i]]));
  }, [pendingLines, selectedPrices, promotion]);
  const selectedTotals =
    pendingQuotes.size > 0 ? sumBundleLines([...pendingQuotes.values()]) : null;

  const runAdd = useCallback(
    async (lines: BundleSelectionLine[]) => {
      const result = await addBundle(lines);
      if (result.status === 'success') {
        setIsModalOpen(false);
      } else {
        selection.refresh();
      }
    },
    [addBundle, selection]
  );

  const proceed = useCallback(() => {
    if (canAdd && pendingLines.every(({ quantity }) => quantity === 1)) {
      void runAdd(selection.lines);
    } else {
      setIsModalOpen(true);
    }
  }, [canAdd, pendingLines, selection.lines, runAdd]);

  const handleAdd = useCallback(() => {
    trackBundleAtcClick({ data: { ...eventData, linkText: addLabel } });
    if (addStatus !== 'partial') reset();
    setArmed(true);
    if (selection.status === 'ready' || selection.status === 'error') {
      proceed();
    } else {
      setPendingAdd(true);
    }
  }, [eventData, addLabel, addStatus, reset, selection.status, proceed]);

  useEffect(() => {
    if (!pendingAdd || selection.status === 'loading') return;
    if (selection.status === 'idle') return;
    setPendingAdd(false);
    proceed();
  }, [pendingAdd, selection.status, proceed]);

  const handleIntent = useCallback(() => setArmed(true), []);

  const failedNames = failed
    .map(
      (line) =>
        products.find(({ product }) => product.parentSku === line.code)?.product
          .title
    )
    .filter(Boolean)
    .join(', ');
  const message =
    addStatus === 'partial'
      ? t('bundleStrip.partialError', { names: failedNames })
      : addStatus === 'error'
      ? t('bundleStrip.error')
      : null;
  const isAdding = addStatus === 'adding' || pendingAdd;

  const formatOptional = (amount: number) =>
    amount > 0 ? formatPrice(amount) : null;
  const formatFrom = (amount: number, isFrom: boolean) => {
    const price = formatPrice(amount) ?? '';
    return isFrom ? t('bundleStrip.fromPrice', { price }) : price;
  };

  const bundlePrice = (index: number): BundleProductPrice | undefined => {
    const { current, original, isRange } = promotedStripLines[index];
    if (current >= stripLines[index].current) return undefined;
    return {
      displayPrice: formatPrice(original) ?? '',
      salePrice: formatFrom(current, isRange),
    };
  };

  const buildYourOwnHref = buildYourOwn
    ? localizeHref(buildYourOwn.url, locale ?? 'en')
    : null;
  const buildYourOwnLabel =
    buildYourOwn?.label || t('bundleStrip.buildYourOwn');
  const buildYourOwnLink = (className: string) =>
    buildYourOwnHref ? (
      <LinkButton
        variant="black"
        href={buildYourOwnHref}
        className={className}
        onClick={(e) =>
          trackLinkClick(e, buildYourOwnHref, (deferral) =>
            trackBundleCtaClick({
              data: {
                ...eventData,
                linkText: buildYourOwnLabel,
                linkUrl: toRelativeUrl(buildYourOwnHref),
              },
              ...deferral,
            })
          )
        }
      >
        {buildYourOwnLabel}
      </LinkButton>
    ) : null;

  return (
    <section className={styles.root} aria-labelledby={headingId}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2 id={headingId} className={styles.heading}>
            {heading}
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        {buildYourOwnLink(styles.linkDesktop)}
      </div>

      <ul className={styles.list}>
        {products.map(({ product, variantSku }, index) => (
          <li key={product.parentSku} className={styles.product}>
            <BundleProductCard
              product={product}
              variantSku={variantSku}
              coOpBrand={coOpBrand}
              atcSource={atcSource}
              bundlePrice={bundlePrice(index)}
              onClick={(e, productUrl) =>
                trackLinkClick(e, productUrl, (deferral) =>
                  trackBundleCardClick({
                    data: {
                      ...eventData,
                      linkText: product.title,
                      linkUrl: toRelativeUrl(productUrl),
                      index,
                    },
                    ...deferral,
                  })
                )
              }
            />

            {index > 0 && (
              <span className={styles.plusTrack} aria-hidden>
                <span className={styles.plus}>
                  <Plus className={styles.plusIcon} weight="regular" />
                </span>
              </span>
            )}
          </li>
        ))}
        <li className={styles.bundle}>
          <BundleCard
            badgeLabel={
              promotion
                ? t('bundleStrip.savePercent', {
                    percent: promotion.percentOff,
                  })
                : badgeLabel
            }
            image={image}
            ctaLabel={addLabel}
            total={formatPrice(stripTotals.current) ?? ''}
            originalTotal={
              stripTotals.savings > 0 ? formatPrice(stripTotals.original) : null
            }
            savings={formatOptional(stripTotals.savings)}
            isFromPrice={stripTotals.isFromPrice}
            isAdding={isAdding}
            message={isModalOpen ? null : message}
            onAdd={handleAdd}
            onIntent={handleIntent}
            priority={priority}
          />
        </li>
      </ul>

      {buildYourOwnLink(styles.linkMobile)}

      <BundleAddModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        products={products}
        status={selection.status}
        lines={pendingLines}
        formatLinePrice={(line) => {
          const price = pendingQuotes.get(line.code);
          if (!price) return { displayPrice: '' };
          const current = price.current * price.quantity;
          const original = price.original * price.quantity;
          const isSale = current < original;
          return {
            displayPrice: isSale
              ? formatPrice(original) ?? ''
              : formatFrom(original, price.isRange),
            salePrice: isSale ? formatFrom(current, price.isRange) : undefined,
          };
        }}
        total={
          selectedTotals
            ? formatFrom(selectedTotals.current, selectedTotals.isFromPrice)
            : null
        }
        canAdd={canAdd}
        isAdding={addStatus === 'adding'}
        message={message}
        onSelectColour={selection.selectColour}
        onSelectSize={selection.selectSize}
        onConfirm={() => void runAdd(selection.lines)}
        onRetry={selection.refresh}
      />
    </section>
  );
};

export default BundleStrip;
